import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import AppShell from "../components/AppShell";
import { api, fmtUSD, formatApiError } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "../components/ui/dialog";
import {
  ArrowLeft, Save, Loader2, ChevronDown, ChevronRight, FileSpreadsheet,
  TrendingUp, TrendingDown, Minus, ScrollText, FileCheck2, Plus, FileEdit,
} from "lucide-react";
import { toast } from "sonner";
import ContractReviewPanel from "../components/ContractReviewPanel";

const STATUS_OPTIONS = ["in-review", "countered", "signed", "abandoned"];

// Color-coded band based on counter status vs P75/P90
const BAND_COLORS = {
  "High":         "bg-emerald-100 text-emerald-800 border-emerald-200",
  "Medium-High":  "bg-blue-50 text-blue-800 border-blue-200",
  "Medium":       "bg-amber-50 text-amber-800 border-amber-200",
  "Low":          "bg-red-50 text-red-800 border-red-200",
  "No-Data":      "bg-slate-100 text-slate-500 border-slate-200",
};

const num = (v) => {
  if (v === "" || v == null) return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};

// strip frontend-only fields the backend doesn't expect
const stripFE = (obj) => {
  const { benchmark, totals, _k, ...rest } = obj || {};
  return rest;
};

export default function NegotiationEditor() {
  const { id } = useParams();
  const [params] = useSearchParams();
  const initialJobId = params.get("review_job") || null;
  const nav = useNavigate();
  const [draft, setDraft] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [activeTab, setActiveTab] = useState("budget"); // budget | review
  const [finalizeOpen, setFinalizeOpen] = useState(false);
  const [sites, setSites] = useState([]);

  // Load sites for the Site selector + finalize dialog
  useEffect(() => {
    api.get("/sites").then((r) => setSites(r.data || [])).catch(() => { /* */ });
  }, []);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get(`/negotiations/${id}`);
      setDraft(r.data);
    } catch (e) {
      toast.error(formatApiError(e));
      nav("/negotiation");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [id]);

  // ----- Editable mutators -----
  const setField = (k, v) => { setDraft((p) => ({ ...p, [k]: v })); setDirty(true); };

  const setVisitField = (vi, k, v) => {
    setDraft((p) => {
      const visits = [...(p.visit_grants || [])];
      visits[vi] = { ...visits[vi], [k]: v };
      return { ...p, visit_grants: visits };
    });
    setDirty(true);
  };

  const setActivityField = (vi, ai, k, v) => {
    setDraft((p) => {
      const visits = [...(p.visit_grants || [])];
      const acts = [...(visits[vi].activities || [])];
      acts[ai] = { ...acts[ai], [k]: v };
      visits[vi] = { ...visits[vi], activities: acts };
      return { ...p, visit_grants: visits };
    });
    setDirty(true);
  };

  const setItemField = (section, i, k, v) => {
    setDraft((p) => {
      const arr = [...(p[section] || [])];
      arr[i] = { ...arr[i], [k]: v };
      return { ...p, [section]: arr };
    });
    setDirty(true);
  };

  // Match counter to a percentile for one click
  const matchToPercentile = (current, percentileVal, setter) => {
    if (percentileVal == null) {
      toast.info("No benchmark data for this item yet.");
      return;
    }
    setter(percentileVal);
  };

  // ----- Save -----
  const save = async () => {
    if (!draft) return;
    setSaving(true);
    try {
      const payload = {
        ...draft,
        visit_grants: (draft.visit_grants || []).map((v) => {
          const cleaned = stripFE(v);
          cleaned.activities = (v.activities || []).map(stripFE);
          return cleaned;
        }),
        invoicables:  (draft.invoicables || []).map(stripFE),
        startup_fees: (draft.startup_fees || []).map(stripFE),
        pass_throughs: (draft.pass_throughs || []).map(stripFE),
      };
      delete payload.totals;
      await api.put(`/negotiations/${id}`, payload);
      toast.success("Saved");
      setDirty(false);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  const exportXlsx = async () => {
    try {
      const r = await api.get(`/negotiations/${id}/export.xlsx`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      const safe = (draft?.study_name || draft?.sponsor || "negotiation").replace(/\s+/g, "_");
      a.download = `${safe}_negotiation.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  // ----- Live totals (re-calc client-side as user types) -----
  const totals = useMemo(() => {
    if (!draft) return null;
    const sumField = (arr, f) => (arr || []).reduce((s, x) => s + (num(x?.[f]) || 0), 0);
    const sV = sumField(draft.visit_grants, "sponsor_proposed");
    const cV = sumField(draft.visit_grants, "stryde_counter");
    const sI = sumField(draft.invoicables, "sponsor_proposed");
    const cI = sumField(draft.invoicables, "stryde_counter");
    const sS = sumField(draft.startup_fees, "sponsor_proposed");
    const cS = sumField(draft.startup_fees, "stryde_counter");
    return {
      visits:      { sponsor: sV, counter: cV, delta: cV - sV },
      invoicables: { sponsor: sI, counter: cI, delta: cI - sI },
      startup:     { sponsor: sS, counter: cS, delta: cS - sS },
      grand_total: { sponsor: sV + sI + sS, counter: cV + cI + cS, delta: (cV + cI + cS) - (sV + sI + sS) },
    };
  }, [draft]);

  if (loading || !draft) {
    return (
      <AppShell title="Negotiation Editor" subtitle="Loading…">
        <div className="p-10 text-center"><Loader2 className="w-6 h-6 mx-auto animate-spin text-slate-400" /></div>
      </AppShell>
    );
  }

  return (
    <AppShell
      title={draft.study_name || "Untitled Negotiation"}
      subtitle={`${draft.sponsor || "Unknown sponsor"} · ${draft.round > 1 ? `Round ${draft.round} · ` : ""}Sponsor Proposed vs Stryde Counter`}
      actions={
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={() => nav("/negotiation")} data-testid="neg-back-btn">
            <ArrowLeft className="w-4 h-4 mr-1.5" /> Back
          </Button>
          <Button variant="outline" onClick={exportXlsx} data-testid="neg-export-btn">
            <FileSpreadsheet className="w-4 h-4 mr-1.5" /> Export XLSX
          </Button>
          <Button onClick={save} disabled={saving || !dirty} variant="outline" data-testid="neg-save-btn">
            {saving ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Save className="w-4 h-4 mr-1.5" />}
            {saving ? "Saving…" : dirty ? "Save" : "Saved"}
          </Button>
          <Button
            onClick={() => setFinalizeOpen(true)}
            disabled={draft.status === "signed"}
            className="bg-emerald-600 hover:bg-emerald-700 text-white"
            data-testid="neg-finalize-btn"
          >
            <FileCheck2 className="w-4 h-4 mr-1.5" />
            {draft.status === "signed" ? "Finalized" : "Finalize & Promote"}
          </Button>
        </div>
      }
    >
      {/* Tab toggle: Budget Editor | Contract Review */}
      <div className="inline-flex border border-slate-200 rounded-md p-0.5 bg-slate-50 mb-5" data-testid="neg-tabs">
        <button
          onClick={() => setActiveTab("budget")}
          className={`inline-flex items-center gap-1.5 px-4 py-1.5 text-sm font-medium rounded transition-colors ${activeTab === "budget" ? "bg-white shadow-sm text-slate-900" : "text-slate-500"}`}
          data-testid="neg-tab-budget"
        >
          <FileSpreadsheet className="w-4 h-4" /> Budget Editor
        </button>
        <button
          onClick={() => setActiveTab("review")}
          className={`inline-flex items-center gap-1.5 px-4 py-1.5 text-sm font-medium rounded transition-colors ${activeTab === "review" ? "bg-white shadow-sm text-slate-900" : "text-slate-500"}`}
          data-testid="neg-tab-review"
        >
          <ScrollText className="w-4 h-4" /> Contract Language Review
        </button>
      </div>

      {activeTab === "budget" && (
        <>
          {/* Meta header */}
          <MetaCard draft={draft} setField={setField} sites={sites} setSites={setSites} />

          {/* Totals summary */}
          <TotalsBar totals={totals} />

          {/* Sections */}
          <div className="space-y-6 mt-6">
            <VisitsSection
              visits={draft.visit_grants || []}
              setVisitField={setVisitField}
              setActivityField={setActivityField}
              matchToPercentile={matchToPercentile}
            />
            <ItemsSection
              title="Invoicables"
              items={draft.invoicables || []}
              section="invoicables"
              setItemField={setItemField}
              matchToPercentile={matchToPercentile}
              totals={totals?.invoicables}
            />
            <ItemsSection
              title="Startup Fees"
              items={draft.startup_fees || []}
              section="startup_fees"
              setItemField={setItemField}
              matchToPercentile={matchToPercentile}
              totals={totals?.startup}
            />
            <ItemsSection
              title="Pass-Throughs"
              items={draft.pass_throughs || []}
              section="pass_throughs"
              setItemField={setItemField}
              matchToPercentile={matchToPercentile}
            />
          </div>
        </>
      )}

      {activeTab === "review" && (
        <ContractReviewPanel negotiationId={id} initialJobId={initialJobId} />
      )}

      <FinalizeDialog
        open={finalizeOpen}
        onOpenChange={setFinalizeOpen}
        draft={draft}
        sites={sites}
        setSites={setSites}
        onFinalized={(cta) => {
          setFinalizeOpen(false);
          toast.success(`Promoted to signed CTA — now part of the benchmark pool.`);
          load();
        }}
      />
    </AppShell>
  );
}

/* ----------- Meta header ----------- */
function MetaCard({ draft, setField, sites, setSites }) {
  const [addSiteOpen, setAddSiteOpen] = useState(false);

  return (
    <div className="bg-white border border-border rounded-md shadow-sm p-5 mb-5" data-testid="neg-meta-card">
      <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
        <div>
          <Label>Study Name</Label>
          <Input value={draft.study_name || ""} onChange={(e) => setField("study_name", e.target.value)} data-testid="meta-study" />
        </div>
        <div>
          <Label>Sponsor</Label>
          <Input value={draft.sponsor || ""} onChange={(e) => setField("sponsor", e.target.value)} data-testid="meta-sponsor" />
        </div>
        <div>
          <Label>PI</Label>
          <Input value={draft.pi_name || ""} onChange={(e) => setField("pi_name", e.target.value)} data-testid="meta-pi" />
        </div>
        <div>
          <Label>Therapeutic Area</Label>
          <Input value={draft.therapeutic_area || ""} onChange={(e) => setField("therapeutic_area", e.target.value)} data-testid="meta-area" />
        </div>
        <div>
          <Label>Protocol #</Label>
          <Input value={draft.protocol_number || ""} onChange={(e) => setField("protocol_number", e.target.value)} data-testid="meta-protocol" />
        </div>
        <div>
          <Label>Phase</Label>
          <Input value={draft.phase || ""} onChange={(e) => setField("phase", e.target.value)} data-testid="meta-phase" />
        </div>
        <div>
          <Label>Site</Label>
          <div className="flex gap-1.5">
            <Select value={draft.site_id || ""} onValueChange={(v) => setField("site_id", v)}>
              <SelectTrigger data-testid="meta-site"><SelectValue placeholder="Select site…" /></SelectTrigger>
              <SelectContent>
                {sites.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
              </SelectContent>
            </Select>
            <Button variant="outline" size="icon" onClick={() => setAddSiteOpen(true)} title="Add new site" data-testid="meta-add-site-btn">
              <Plus className="w-4 h-4" />
            </Button>
          </div>
        </div>
        <div>
          <Label>Status</Label>
          <Select value={draft.status || "in-review"} onValueChange={(v) => setField("status", v)}>
            <SelectTrigger data-testid="meta-status"><SelectValue /></SelectTrigger>
            <SelectContent>
              {STATUS_OPTIONS.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>
      {draft.source_filename && (
        <div className="mt-3 text-[11px] text-slate-500">
          Source: <span className="font-mono">{draft.source_filename}</span>
        </div>
      )}

      <AddSiteDialog
        open={addSiteOpen}
        onOpenChange={setAddSiteOpen}
        onCreated={(site) => {
          setSites((prev) => [...prev, site]);
          setField("site_id", site.id);
          setAddSiteOpen(false);
        }}
      />
    </div>
  );
}

/* ----------- Add Site Dialog ----------- */
function AddSiteDialog({ open, onOpenChange, onCreated }) {
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);

  const create = async () => {
    if (!name.trim()) return toast.error("Site name is required");
    setBusy(true);
    try {
      const r = await api.post("/sites", { name: name.trim() });
      toast.success(`Created site "${r.data.name}"`);
      setName("");
      onCreated(r.data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="add-site-dialog">
        <DialogHeader>
          <DialogTitle>Add New Site</DialogTitle>
          <DialogDescription>Create a new research site that will be available across all CTAs and negotiations.</DialogDescription>
        </DialogHeader>
        <div className="space-y-2">
          <Label>Site Name *</Label>
          <Input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g. Stryde Research — Tampa"
            data-testid="add-site-name"
            autoFocus
          />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={create} disabled={busy} className="bg-[#0A4C95] hover:bg-[#083d79]" data-testid="add-site-save">
            {busy ? "Creating…" : "Create site"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* ----------- Finalize → Promote to Signed CTA ----------- */
function FinalizeDialog({ open, onOpenChange, draft, sites, setSites, onFinalized }) {
  const [siteId, setSiteId] = useState(draft?.site_id || "");
  const [signedFilename, setSignedFilename] = useState("");
  const [busy, setBusy] = useState(false);
  const [addSiteOpen, setAddSiteOpen] = useState(false);

  useEffect(() => { setSiteId(draft?.site_id || ""); }, [draft?.site_id, open]);

  const totalCounter = useMemo(() => {
    if (!draft) return 0;
    const sumField = (arr, f) => (arr || []).reduce((s, x) => s + (Number(x?.[f]) || 0), 0);
    return (
      sumField(draft.visit_grants, "stryde_counter") +
      sumField(draft.invoicables, "stryde_counter") +
      sumField(draft.startup_fees, "stryde_counter")
    );
  }, [draft]);

  const finalize = async () => {
    if (!siteId) return toast.error("Pick a site for the signed CTA.");
    setBusy(true);
    try {
      await api.post(`/negotiations/${draft.id}/finalize`, {
        site_id: siteId,
        signed_pdf_filename: signedFilename || undefined,
      });
      onFinalized();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="finalize-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileCheck2 className="w-5 h-5 text-emerald-600" /> Finalize Negotiation
          </DialogTitle>
          <DialogDescription>
            Promote the final Stryde Counter numbers to a <strong>signed CTA</strong> in your library.
            Only signed CTAs feed the benchmark / rate-card pool.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <div className="rounded-md border border-emerald-200 bg-emerald-50/40 px-4 py-3 text-sm">
            <div className="text-xs text-emerald-800 uppercase tracking-wider font-semibold">Final Budget</div>
            <div className="font-mono-fin text-2xl font-bold text-emerald-900 mt-1">{fmtUSD(totalCounter)}</div>
            <div className="text-[11px] text-emerald-700 mt-0.5">Sum of all Stryde Counter values across visits, invoicables, and startup fees.</div>
          </div>

          <div>
            <Label>Site *</Label>
            <div className="flex gap-1.5">
              <Select value={siteId} onValueChange={setSiteId}>
                <SelectTrigger data-testid="finalize-site-select"><SelectValue placeholder="Select site…" /></SelectTrigger>
                <SelectContent>
                  {sites.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
                </SelectContent>
              </Select>
              <Button variant="outline" size="icon" onClick={() => setAddSiteOpen(true)} title="Add new site">
                <Plus className="w-4 h-4" />
              </Button>
            </div>
          </div>

          <div>
            <Label>Signed PDF filename <span className="text-slate-400 font-normal">(optional, for reference)</span></Label>
            <Input
              value={signedFilename}
              onChange={(e) => setSignedFilename(e.target.value)}
              placeholder="e.g. PFE-A4091058_signed_2026-06-25.pdf"
              data-testid="finalize-pdf-filename"
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={finalize} disabled={busy || !siteId} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="finalize-confirm-btn">
            {busy ? "Promoting…" : "Promote to Signed CTA"}
          </Button>
        </DialogFooter>

        <AddSiteDialog
          open={addSiteOpen}
          onOpenChange={setAddSiteOpen}
          onCreated={(site) => { setSites((prev) => [...prev, site]); setSiteId(site.id); setAddSiteOpen(false); }}
        />
      </DialogContent>
    </Dialog>
  );
}

/* ----------- Totals bar ----------- */
function TotalsCard({ label, t }) {
  return (
    <div className="bg-white border border-border rounded-md p-4 shadow-sm">
      <div className="text-[10.5px] uppercase tracking-wider text-slate-500 font-semibold">{label}</div>
      <div className="mt-2 grid grid-cols-3 gap-2 text-[12px]">
        <div>
          <div className="text-slate-500">Sponsor</div>
          <div className="font-mono-fin font-semibold text-slate-900">{fmtUSD(t.sponsor)}</div>
        </div>
        <div>
          <div className="text-slate-500">Counter</div>
          <div className="font-mono-fin font-semibold text-[#0A4C95]">{fmtUSD(t.counter)}</div>
        </div>
        <div>
          <div className="text-slate-500">Δ</div>
          <div className={`font-mono-fin font-semibold ${t.delta > 0 ? "text-emerald-700" : t.delta < 0 ? "text-red-700" : "text-slate-500"}`}>
            {t.delta === 0 ? "—" : `${t.delta > 0 ? "+" : ""}${fmtUSD(t.delta)}`}
          </div>
        </div>
      </div>
    </div>
  );
}

function TotalsBar({ totals }) {
  if (!totals) return null;
  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="neg-totals-bar">
      <TotalsCard label="Visit Grants" t={totals.visits} />
      <TotalsCard label="Invoicables" t={totals.invoicables} />
      <TotalsCard label="Startup Fees" t={totals.startup} />
      <TotalsCard label="Grand Total" t={totals.grand_total} />
    </div>
  );
}

/* ----------- Inline benchmark cell ----------- */
function PctBtn({ k, v, onMatch }) {
  return (
    <button
      type="button"
      onClick={() => onMatch(v)}
      title={`Match counter to ${k}`}
      className="px-1.5 py-0.5 rounded hover:bg-slate-100 text-[10.5px] font-mono text-slate-700 transition-colors"
    >
      <span className="text-slate-400 mr-1">{k}</span>{fmtUSD(v)}
    </button>
  );
}

function BenchmarkCell({ bench, onMatch }) {
  if (!bench) return <td className="px-3 py-2 text-xs text-slate-400">—</td>;
  const hasData = (bench.rate_card_n || 0) > 0 && bench.p75 != null;
  if (!hasData) {
    return (
      <td className="px-3 py-2 text-[11px] text-slate-400" data-testid="bench-nodata">
        <Badge variant="outline" className={BAND_COLORS["No-Data"]}>No benchmark</Badge>
      </td>
    );
  }
  const band = bench.counter_status || "No-Data";
  const icon = band === "High" || band === "Medium-High" ? <TrendingUp className="w-3 h-3" /> :
               band === "Low" ? <TrendingDown className="w-3 h-3" /> : <Minus className="w-3 h-3" />;

  return (
    <td className="px-3 py-2 text-[11px]" data-testid="bench-cell">
      <div className="flex items-center gap-1 flex-wrap">
        <Badge variant="outline" className={`${BAND_COLORS[band]} inline-flex items-center gap-1`}>{icon}{band}</Badge>
        <PctBtn k="P75" v={bench.p75} onMatch={onMatch} />
        <PctBtn k="P80" v={bench.p80} onMatch={onMatch} />
        <PctBtn k="P90" v={bench.p90} onMatch={onMatch} />
        <span className="text-slate-400 text-[10px]">n={bench.rate_card_n}</span>
      </div>
    </td>
  );
}

/* ----------- Item amount input ----------- */
function AmountInput({ value, onChange, testid }) {
  return (
    <Input
      type="number"
      value={value ?? ""}
      onChange={(e) => onChange(num(e.target.value))}
      className="h-8 text-right font-mono-fin text-sm"
      data-testid={testid}
    />
  );
}

/* ----------- Visits section ----------- */
function VisitsSection({ visits, setVisitField, setActivityField, matchToPercentile }) {
  const [expanded, setExpanded] = useState({});
  if (!visits.length) return null;
  return (
    <div className="bg-white border border-border rounded-md shadow-sm overflow-hidden">
      <div className="px-5 py-3.5 border-b border-border bg-slate-50/50">
        <h3 className="font-semibold text-slate-900">Visit Grants <span className="text-xs text-slate-500 font-normal">— {visits.length} visit{visits.length === 1 ? "" : "s"}</span></h3>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50/70 border-b border-slate-200 text-[11px] uppercase tracking-wider text-slate-600">
            <tr>
              <th className="text-left px-3 py-2 w-8"></th>
              <th className="text-left px-3 py-2">Visit</th>
              <th className="text-right px-3 py-2 w-32">Sponsor Proposed</th>
              <th className="text-right px-3 py-2 w-32">Stryde Counter</th>
              <th className="text-right px-3 py-2 w-20">Δ</th>
              <th className="text-left px-3 py-2 w-[440px]">Benchmark</th>
            </tr>
          </thead>
          <tbody>
            {visits.map((v, vi) => {
              const hasActs = (v.activities || []).length > 0;
              const open = expanded[vi] ?? true;
              const sp = num(v.sponsor_proposed);
              const ct = num(v.stryde_counter);
              const delta = (ct || 0) - (sp || 0);
              return (
                <React.Fragment key={vi}>
                  <tr className="border-t border-slate-100 hover:bg-slate-50/40" data-testid={`visit-row-${vi}`}>
                    <td className="px-3 py-2 align-top">
                      {hasActs ? (
                        <button onClick={() => setExpanded({ ...expanded, [vi]: !open })} className="text-slate-400 hover:text-slate-700">
                          {open ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                        </button>
                      ) : null}
                    </td>
                    <td className="px-3 py-2 align-top">
                      <Input
                        value={v.visit_name || ""}
                        onChange={(e) => setVisitField(vi, "visit_name", e.target.value)}
                        className="h-8 text-sm font-semibold"
                        data-testid={`visit-name-${vi}`}
                      />
                      {v.visit_code && <div className="text-[10.5px] text-slate-500 mt-1">{v.visit_code}</div>}
                    </td>
                    <td className="px-3 py-2 align-top">
                      <AmountInput value={sp} onChange={(n) => setVisitField(vi, "sponsor_proposed", n)} testid={`visit-sp-${vi}`} />
                    </td>
                    <td className="px-3 py-2 align-top">
                      <AmountInput value={ct} onChange={(n) => setVisitField(vi, "stryde_counter", n)} testid={`visit-ct-${vi}`} />
                    </td>
                    <td className={`px-3 py-2 text-right font-mono-fin text-sm align-top ${delta > 0 ? "text-emerald-700" : delta < 0 ? "text-red-700" : "text-slate-400"}`}>
                      {delta === 0 ? "—" : `${delta > 0 ? "+" : ""}${fmtUSD(delta)}`}
                    </td>
                    <BenchmarkCell
                      bench={v.benchmark}
                      onMatch={(val) => matchToPercentile(ct, val, (n) => setVisitField(vi, "stryde_counter", n))}
                    />
                  </tr>

                  {open && hasActs && (v.activities || []).map((a, ai) => {
                    const asp = num(a.sponsor_proposed);
                    const act = num(a.stryde_counter);
                    const ad = (act || 0) - (asp || 0);
                    return (
                      <tr key={`a-${vi}-${ai}`} className="border-t border-slate-100 bg-slate-50/30" data-testid={`activity-row-${vi}-${ai}`}>
                        <td></td>
                        <td className="px-3 py-1.5 pl-10 align-top">
                          <Input
                            value={a.name || ""}
                            onChange={(e) => setActivityField(vi, ai, "name", e.target.value)}
                            className="h-7 text-[12px]"
                            data-testid={`activity-name-${vi}-${ai}`}
                          />
                        </td>
                        <td className="px-3 py-1.5 align-top">
                          <AmountInput value={asp} onChange={(n) => setActivityField(vi, ai, "sponsor_proposed", n)} testid={`activity-sp-${vi}-${ai}`} />
                        </td>
                        <td className="px-3 py-1.5 align-top">
                          <AmountInput value={act} onChange={(n) => setActivityField(vi, ai, "stryde_counter", n)} testid={`activity-ct-${vi}-${ai}`} />
                        </td>
                        <td className={`px-3 py-1.5 text-right font-mono-fin text-[12px] align-top ${ad > 0 ? "text-emerald-700" : ad < 0 ? "text-red-700" : "text-slate-400"}`}>
                          {ad === 0 ? "—" : `${ad > 0 ? "+" : ""}${fmtUSD(ad)}`}
                        </td>
                        <BenchmarkCell
                          bench={a.benchmark}
                          onMatch={(val) => matchToPercentile(act, val, (n) => setActivityField(vi, ai, "stryde_counter", n))}
                        />
                      </tr>
                    );
                  })}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/* ----------- Generic items section (invoicables / startup_fees / pass_throughs) ----------- */
function ItemsSection({ title, items, section, setItemField, matchToPercentile }) {
  if (!items.length) return null;
  return (
    <div className="bg-white border border-border rounded-md shadow-sm overflow-hidden" data-testid={`section-${section}`}>
      <div className="px-5 py-3.5 border-b border-border bg-slate-50/50">
        <h3 className="font-semibold text-slate-900">{title} <span className="text-xs text-slate-500 font-normal">— {items.length} item{items.length === 1 ? "" : "s"}</span></h3>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50/70 border-b border-slate-200 text-[11px] uppercase tracking-wider text-slate-600">
            <tr>
              <th className="text-left px-3 py-2">Item</th>
              <th className="text-right px-3 py-2 w-32">Sponsor Proposed</th>
              <th className="text-right px-3 py-2 w-32">Stryde Counter</th>
              <th className="text-right px-3 py-2 w-20">Δ</th>
              <th className="text-left px-3 py-2 w-[440px]">Benchmark</th>
            </tr>
          </thead>
          <tbody>
            {items.map((it, i) => {
              const sp = num(it.sponsor_proposed);
              const ct = num(it.stryde_counter);
              const delta = (ct || 0) - (sp || 0);
              return (
                <tr key={i} className="border-t border-slate-100 hover:bg-slate-50/40" data-testid={`${section}-row-${i}`}>
                  <td className="px-3 py-2 align-top">
                    <Input
                      value={it.name || ""}
                      onChange={(e) => setItemField(section, i, "name", e.target.value)}
                      className="h-8 text-sm"
                      data-testid={`${section}-name-${i}`}
                    />
                    {(it.category || it.when_billable) && (
                      <div className="text-[10.5px] text-slate-500 mt-1">
                        {it.category || ""}{it.category && it.when_billable ? " · " : ""}{it.when_billable || ""}
                      </div>
                    )}
                  </td>
                  <td className="px-3 py-2 align-top">
                    <AmountInput value={sp} onChange={(n) => setItemField(section, i, "sponsor_proposed", n)} testid={`${section}-sp-${i}`} />
                  </td>
                  <td className="px-3 py-2 align-top">
                    <AmountInput value={ct} onChange={(n) => setItemField(section, i, "stryde_counter", n)} testid={`${section}-ct-${i}`} />
                  </td>
                  <td className={`px-3 py-2 text-right font-mono-fin text-sm align-top ${delta > 0 ? "text-emerald-700" : delta < 0 ? "text-red-700" : "text-slate-400"}`}>
                    {delta === 0 ? "—" : `${delta > 0 ? "+" : ""}${fmtUSD(delta)}`}
                  </td>
                  <BenchmarkCell
                    bench={it.benchmark}
                    onMatch={(val) => matchToPercentile(ct, val, (n) => setItemField(section, i, "stryde_counter", n))}
                  />
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// Helper export retained for any future imports
